import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { activeTasksQueryOptions, taskProgressKeys } from "@/lib/queries/task-progress.queries";
import { personaOpportunitySetKeys } from "@/lib/queries/persona-opportunity-sets.queries";
import { cancelTask, dismissTask } from "@/features/task-progress/api/task-progress.api";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { PersonaOpportunitySetListPage } from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import { suppressPersonaOpportunitySetId, unsuppressPersonaOpportunitySetId } from "@/features/persona-opportunities/lib/persona-opportunity-set-suppression";

/** Prefix matching every tenant's persona opportunity-sets list cache. */
const SETS_LIST_PREFIX = [...personaOpportunitySetKeys.all, "list"] as const;

/**
 * Cancels in-flight persona opportunity-set generation for a setId.
 * Same cancel → dismiss sequence as the task sidebar Stop button, but invalidates
 * `personaOpportunitySetKeys` list cache (not legacy opportunity sets).
 *
 * Returns true when a cancellable task was found and cancellation was requested.
 * `onCancelled` runs only after cancel + dismiss succeed.
 */
export function useStopPersonaOpportunityGeneration(): (
  setId: string,
  onCancelled?: (correlationId: string) => void,
) => boolean {
  const userEmail = useAuth((s) => s.user?.email);
  const queryClient = useQueryClient();

  const { data: activeTasks = [] } = useQuery(activeTasksQueryOptions(userEmail ?? undefined));

  const cancelMutation = useMutation({
    mutationFn: async ({ correlationId }: { correlationId: string; setId: string }) => {
      await cancelTask(correlationId);
      await dismissTask(correlationId).catch(() => {});
    },
    onMutate: ({ setId }) => {
      // Suppress before list invalidations (delete / poll) can refetch the still-
      // GENERATING row and flash the card back — same race as legacy opportunity sets.
      suppressPersonaOpportunitySetId(setId);
      queryClient.setQueriesData<PersonaOpportunitySetListPage>(
        { queryKey: SETS_LIST_PREFIX },
        (prev) => {
          if (!prev || !Array.isArray(prev.items)) return prev;
          return { ...prev, items: prev.items.filter((set) => set.id !== setId) };
        },
      );
      // Drop the workspace detail cache so a cancelled GENERATING set cannot keep
      // painting the workspace from stale data after the list row is removed.
      queryClient.removeQueries({
        queryKey: personaOpportunitySetKeys.details(),
        predicate: (query) => query.queryKey[3] === setId,
      });
    },
    onSuccess: () => {
      // Do NOT invalidate the sets list here — refetch would race the worker
      // soft-delete and restore the GENERATING card (see suppress helper).
      if (userEmail) {
        void queryClient.invalidateQueries({ queryKey: taskProgressKeys.active(userEmail) });
      }
    },
    onError: (_err, { setId }) => {
      // Cancel failed — allow the set back into the list.
      unsuppressPersonaOpportunitySetId(setId);
      if (userEmail) {
        void queryClient.invalidateQueries({ queryKey: taskProgressKeys.active(userEmail) });
      }
      void queryClient.invalidateQueries({ queryKey: SETS_LIST_PREFIX });
    },
  });

  return useCallback(
    (setId: string, onCancelled?: (correlationId: string) => void) => {
      const task = activeTasks.find(
        (t) =>
          t.type === "generate-persona-opportunity-set" &&
          t.cancellable === true &&
          t.navigation?.params?.setId === setId,
      );
      if (!task) return false;
      cancelMutation.mutate(
        { correlationId: task.correlationId, setId },
        { onSuccess: () => onCancelled?.(task.correlationId) },
      );
      return true;
    },
    [activeTasks, cancelMutation],
  );
}
